import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import CookieBanner from '../components/CookieBanner';

const ATUALIZADO = '12 de outubro de 2026';
const EMPRESA = 'Akuant Solutions';

const P = ({ children }) => <p className="text-slate-600 leading-relaxed">{children}</p>;
const UL = ({ items }) => <ul className="list-disc pl-5 space-y-1.5 text-slate-600 leading-relaxed">{items.map((i) => <li key={i}>{i}</li>)}</ul>;
const Contato = () => <>pelo formulário de contato da <Link to="/#contato" className="font-semibold text-emerald-600 hover:underline">página inicial</Link></>;

const DOCS = {
  '/privacidade': {
    title: 'Política de Privacidade',
    intro: `Esta política explica como o Gastify, operado pela ${EMPRESA}, trata dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018, a LGPD).`,
    sections: [
      ['1. Quem é o controlador', <P>A {EMPRESA} é a controladora dos dados coletados nesta página e na sua conta de uso do Gastify. Quando uma empresa contrata o Gastify para a sua equipe, essa empresa é a controladora dos dados dos seus usuários e a {EMPRESA} atua como operadora.</P>],
      ['2. Dados que coletamos', <UL items={[
        'Pedido de acesso: nome, e-mail, WhatsApp, tipo de uso (pessoa física ou empresa), nome da empresa e mensagem opcional.',
        'Conta de uso: nome de usuário, nome completo, e-mail, foto de perfil (opcional) e senha, que é guardada de forma criptografada.',
        'Dados financeiros que você mesmo cadastra: lançamentos, contas, cartões, categorias, metas, tarefas e notas.',
        'Dados técnicos: endereço IP, dispositivo, navegador e horários de acesso, usados para segurança e para a lista de dispositivos conectados.',
        'Conversas com o assistente de IA, usadas para entender e registrar o que você pede.',
      ]} />],
      ['3. Para que usamos e em que base legal', <UL items={[
        'Responder ao seu pedido de acesso e entrar em contato: consentimento e procedimentos preliminares a contrato.',
        'Prestar o serviço contratado e manter a sua conta: execução de contrato.',
        'Segurança, prevenção a fraudes e limite de tentativas de acesso: legítimo interesse.',
        'Estatísticas de uso do site, apenas se você permitir: consentimento.',
        'Cumprir obrigações legais e regulatórias: obrigação legal.',
      ]} />],
      ['4. Com quem compartilhamos', <><P>Não vendemos dados pessoais. Compartilhamos apenas o necessário com fornecedores que nos ajudam a operar o serviço, como hospedagem e infraestrutura, e provedores de inteligência artificial usados pelo assistente, que recebem o texto da conversa para gerar a resposta. Também podemos compartilhar dados quando a lei ou uma autoridade exigir.</P></>],
      ['5. Por quanto tempo guardamos', <P>Guardamos os dados enquanto a conta estiver ativa e pelo tempo necessário para cumprir obrigações legais. Pedidos de acesso não aprovados podem ser descartados. Ao encerrar a conta, os dados são excluídos ou anonimizados, salvo quando a lei exigir a guarda.</P>],
      ['6. Segurança', <UL items={[
        'Cada cliente tem o seu próprio espaço de dados, isolado dos demais.',
        'Senhas armazenadas com criptografia e limite de tentativas de login.',
        'Controle de dispositivos conectados, com encerramento de sessões à distância.',
        'Comunicação protegida por HTTPS.',
      ]} />],
      ['7. Seus direitos', <><P>Você pode solicitar, a qualquer momento: confirmação de que tratamos seus dados, acesso, correção, anonimização, bloqueio ou eliminação, portabilidade, informação sobre compartilhamento e revogação do consentimento. Veja como exercer esses direitos na página <Link to="/lgpd" className="font-semibold text-emerald-600 hover:underline">Seus direitos (LGPD)</Link>.</P></>],
      ['8. Crianças e adolescentes', <P>O Gastify não é destinado a menores de 18 anos sem acompanhamento de um responsável legal.</P>],
      ['9. Mudanças nesta política', <P>Podemos atualizar esta política. A data da última atualização fica sempre no topo da página e, em mudanças relevantes, avisaremos pelos canais de contato.</P>],
      ['10. Contato', <P>Dúvidas sobre privacidade? Fale com a gente <Contato />.</P>],
    ],
  },
  '/termos': {
    title: 'Termos de Uso',
    intro: `Ao usar o Gastify, você concorda com estes termos. Leia com atenção. O serviço é oferecido pela ${EMPRESA}.`,
    sections: [
      ['1. O que é o Gastify', <P>Uma plataforma online de gestão financeira para pessoas e empresas, com lançamentos, contas, cartões, metas, relatórios e assistente de inteligência artificial.</P>],
      ['2. Acesso e conta', <UL items={[
        'O acesso é liberado pela nossa equipe depois de um contato com você.',
        'Você é responsável por manter sua senha em segredo e por tudo o que acontece na sua conta.',
        'Informe dados verdadeiros e mantenha o cadastro atualizado.',
        'Avise-nos se suspeitar de uso indevido da sua conta.',
      ]} />],
      ['3. Uso permitido', <UL items={[
        'Usar o serviço apenas para fins lícitos e de acordo com estes termos.',
        'Não tentar acessar dados de outros clientes, burlar a segurança, copiar ou fazer engenharia reversa do sistema.',
        'Não usar o serviço para fraudes, lavagem de dinheiro ou qualquer atividade ilegal.',
      ]} />],
      ['4. Seus dados', <P>Os dados financeiros que você cadastra continuam sendo seus. Usamos esses dados somente para prestar o serviço, conforme a nossa <Link to="/privacidade" className="font-semibold text-emerald-600 hover:underline">Política de Privacidade</Link>.</P>],
      ['5. Assistente de inteligência artificial', <P>O assistente interpreta o que você escreve para registrar lançamentos e responder perguntas. Ele pode errar. Confira os registros importantes. As respostas são informativas e não substituem aconselhamento financeiro, contábil, jurídico ou de investimentos profissional.</P>],
      ['6. Disponibilidade', <P>Trabalhamos para manter o serviço disponível, mas ele pode ter interrupções para manutenção, falhas de terceiros ou eventos fora do nosso controle. Podemos alterar ou descontinuar funcionalidades, avisando quando possível.</P>],
      ['7. Planos e pagamento', <P>As condições comerciais, quando houver cobrança, serão combinadas com você antes da contratação.</P>],
      ['8. Limitação de responsabilidade', <P>O Gastify é uma ferramenta de organização. Não nos responsabilizamos por decisões financeiras tomadas com base nas informações exibidas, nem por perdas decorrentes de uso indevido da conta, senhas compartilhadas ou dados incorretos inseridos pelo usuário, na extensão permitida pela lei.</P>],
      ['9. Suspensão e encerramento', <P>Podemos suspender ou encerrar contas que violem estes termos ou a lei. Você pode pedir o encerramento da sua conta a qualquer momento.</P>],
      ['10. Alterações e foro', <P>Podemos atualizar estes termos, e a versão vigente fica sempre nesta página. Estes termos são regidos pela lei brasileira, e fica eleito o foro da comarca do domicílio do usuário consumidor, ou da sede da {EMPRESA} nos demais casos.</P>],
      ['11. Contato', <P>Dúvidas sobre os termos? Fale com a gente <Contato />.</P>],
    ],
  },
  '/cookies': {
    title: 'Política de Cookies',
    intro: 'Cookies e tecnologias parecidas são pequenos arquivos ou registros guardados no seu navegador. Veja como o Gastify os usa.',
    sections: [
      ['1. Categorias que usamos', <UL items={[
        'Essenciais (sempre ativos): mantêm você conectado, protegem sua conta, guardam o tema claro ou escuro e permitem que o aplicativo funcione e seja instalado no celular. Sem eles o serviço não funciona.',
        'Estatísticas (somente com a sua permissão): ajudam a entender quais partes do site são mais usadas, de forma agregada.',
      ]} />],
      ['2. O que fica guardado no seu navegador', <UL items={[
        'Sessão de login e identificação do dispositivo (essencial).',
        'Preferência de tema (essencial).',
        'Cópia de arquivos do aplicativo para carregar mais rápido e funcionar como app (essencial).',
        'Sua escolha sobre cookies, para não perguntarmos de novo a cada visita (essencial).',
      ]} />],
      ['3. Como gerenciar sua escolha', <><P>Você pode mudar a decisão quando quiser, pelo botão abaixo, e também apagar os dados do site nas configurações do seu navegador.</P>
        <button onClick={() => window.dispatchEvent(new Event('gastify:cookie-open'))} className="mt-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-5 py-2.5" data-testid="cookie-reabrir">Alterar minhas preferências de cookies</button></>],
      ['4. Cookies de terceiros', <P>No momento não carregamos ferramentas de publicidade. Se passarmos a usar ferramentas de estatística de terceiros, elas só serão ativadas com o seu consentimento e esta política será atualizada.</P>],
      ['5. Contato', <P>Dúvidas sobre cookies? Fale com a gente <Contato />.</P>],
    ],
  },
  '/lgpd': {
    title: 'Seus direitos (LGPD)',
    intro: 'A Lei Geral de Proteção de Dados garante a você controle sobre os seus dados pessoais. Veja o que você pode pedir e como.',
    sections: [
      ['1. O que você pode pedir', <UL items={[
        'Confirmar se tratamos seus dados e acessá-los.',
        'Corrigir dados incompletos, inexatos ou desatualizados.',
        'Anonimizar, bloquear ou eliminar dados desnecessários ou tratados fora da lei.',
        'Receber seus dados em formato estruturado (portabilidade). Dentro do aplicativo, você já pode exportar seus lançamentos em CSV, Excel ou PDF.',
        'Saber com quem compartilhamos seus dados.',
        'Revogar o consentimento a qualquer momento, sem prejuízo do tratamento já feito.',
        'Pedir a revisão de decisões tomadas apenas de forma automatizada.',
      ]} />],
      ['2. Como fazer um pedido', <P>Envie sua solicitação <Contato />, informando que se trata de um pedido da LGPD e qual direito deseja exercer. Para sua segurança, podemos pedir uma confirmação de identidade.</P>],
      ['3. Prazo de resposta', <P>Respondemos o mais rápido possível e dentro dos prazos previstos na lei.</P>],
      ['4. Encarregado de dados', <P>Os pedidos de titulares são recebidos pelo canal acima e tratados pela equipe responsável pela proteção de dados da {EMPRESA}.</P>],
      ['5. Reclamação à autoridade', <P>Se achar que o tratamento dos seus dados não está correto, você também pode recorrer à Autoridade Nacional de Proteção de Dados (ANPD), em gov.br/anpd.</P>],
      ['6. Documentos relacionados', <UL items={['Política de Privacidade', 'Termos de Uso', 'Política de Cookies']} />],
    ],
  },
};

export const LEGAL_LINKS = [
  ['/privacidade', 'Política de Privacidade'],
  ['/termos', 'Termos de Uso'],
  ['/cookies', 'Política de Cookies'],
  ['/lgpd', 'Seus direitos (LGPD)'],
];

export default function LegalPage() {
  const { pathname } = useLocation();
  const doc = DOCS[pathname.replace(/\/$/, '')] || DOCS['/privacidade'];

  useEffect(() => { window.scrollTo(0, 0); document.title = `${doc.title} · Gastify`; }, [doc]);
  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains('dark');
    const force = () => { if (root.classList.contains('dark')) root.classList.remove('dark'); };
    force();
    const obs = new MutationObserver(force);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => { obs.disconnect(); if (had) root.classList.add('dark'); };
  }, []);

  return (
    <div className="min-h-screen bg-[#EAEFF5] text-slate-800 antialiased" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }} data-testid="legal-page">
      <header className="bg-[#0B1020] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/gastify-icon-192.png" alt="" className="w-9 h-9 rounded-xl" />
            <span className="text-lg font-extrabold text-white tracking-tight">Gastify</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft size={15} />Voltar ao site</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <article className="bg-white rounded-[28px] shadow-sm p-6 sm:p-10 space-y-7">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{doc.title}</h1>
            <p className="text-xs text-slate-400 mt-2">Última atualização: {ATUALIZADO}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{doc.intro}</p>
          </div>
          {doc.sections.map(([h, body]) => (
            <section key={h} className="space-y-2.5">
              <h2 className="text-lg font-bold text-slate-900">{h}</h2>
              {body}
            </section>
          ))}
        </article>

        <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-2 justify-center text-sm" aria-label="Documentos legais">
          {LEGAL_LINKS.map(([to, label]) => (
            <Link key={to} to={to} className={`font-semibold ${pathname.replace(/\/$/, '') === to ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-800'}`}>{label}</Link>
          ))}
        </nav>
        <p className="mt-6 text-center text-xs text-slate-400">Gastify · feito por {EMPRESA} © 2026</p>
      </main>
      <CookieBanner />
    </div>
  );
}
